/**
 * usePaywall Hook
 *
 * Centralizes paywall state so any component can trigger it.
 * Usage:
 *   const { openPaywall } = usePaywall();
 *   if (!isPremium) openPaywall();
 */

import { useState, useCallback, createContext, useContext, ReactNode } from 'react';

interface PaywallContextType {
  isPaywallOpen: boolean;
  openPaywall: () => void;
  closePaywall: () => void;
}

const PaywallContext = createContext<PaywallContextType>({
  isPaywallOpen: false,
  openPaywall: () => {},
  closePaywall: () => {},
});

export function PaywallProvider({ children }: { children: ReactNode }) {
  const [isPaywallOpen, setIsPaywallOpen] = useState(false);

  const openPaywall = useCallback(() => {
    setIsPaywallOpen(true);
  }, []);

  const closePaywall = useCallback(() => {
    setIsPaywallOpen(false);
  }, []);

  return (
    <PaywallContext.Provider value={{ isPaywallOpen, openPaywall, closePaywall }}>
      {children}
    </PaywallContext.Provider>
  );
}

export function usePaywall() {
  const context = useContext(PaywallContext);
  if (!context) {
    throw new Error('usePaywall must be used within PaywallProvider');
  }
  return context;
}
